import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { setGlobalQueryTracker } from '../api';

const QueryContext = createContext();

export const useQueryContext = () => {
  const context = useContext(QueryContext);
  if (!context) {
    throw new Error('useQueryContext must be used within a QueryProvider');
  }
  return context;
};

export const QueryProvider = ({ children }) => {
  const [lastQuery, setLastQuery] = useState(null);
  const [, setActiveQueries] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [slowQueryWarning, setSlowQueryWarning] = useState(false);
  const timeoutRef = useRef(null);

  const updateLastQuery = useCallback((query) => {
    setLastQuery(query);
  }, []);

  const startQuery = useCallback((queryId) => {
    setActiveQueries(prev => {
      const newSet = new Set(prev);
      newSet.add(queryId);
      return newSet;
    });
    setIsLoading(true);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout for slow query warning (2 seconds)
    timeoutRef.current = setTimeout(() => {
      setSlowQueryWarning(true);
    }, 2000);
  }, []);

  const endQuery = useCallback((queryId) => {
    setActiveQueries(prev => {
      const newSet = new Set(prev);
      newSet.delete(queryId);
      
      // If no more active queries, clear loading state
      if (newSet.size === 0) {
        setIsLoading(false);
        setSlowQueryWarning(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
      
      return newSet;
    });
  }, []);

  // Connect the API wrapper to this context
  useEffect(() => {
    setGlobalQueryTracker(updateLastQuery);
  }, [updateLastQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const value = {
    lastQuery,
    isLoading,
    slowQueryWarning,
    startQuery,
    endQuery,
  };

  return (
    <QueryContext.Provider value={value}>
      {children}
    </QueryContext.Provider>
  );
}; 